/**
 * CENTRAL PRICING DATA SOURCE
 * Single source of truth for ALL Crescent Electrical pricing
 * 
 * IMPORTANT: All prices are verified and extracted from existing website
 * DO NOT modify prices without approval
 */

// Company Information
export const companyInfo = {
  name: "Crescent Electrical Ltd",
  phone: "07711 233685",
  whatsapp: "447711233685",
  email: "info@electriccertificate.co.uk", // NOTE: Requires verification
  serviceArea: "Greater London",
  googleRating: 5.0,
  reviewCount: 13,
  accreditation: "NICEIC Approved Contractor",
  businessHours: "Open until 9pm"
};

// REWIRE PRICING - Verified from existing website
export const rewirePricing = [
  {
    id: 'rewire-1-2-bed',
    propertyType: '1–2 Bed Flat',
    bedrooms: [1, 2],
    propertyTypes: ['flat'],
    price: '£4,950',
    priceNumeric: 4950,
    priceRange: null,
    note: 'Empty property, standard spec, white accessories, average access'
  },
  {
    id: 'rewire-3-bed',
    propertyType: '3 Bed House',
    bedrooms: [3],
    propertyTypes: ['house', 'terraced', 'semi-detached'],
    price: '£6,050 – £7,150',
    priceNumeric: 6050,
    priceRange: { min: 6050, max: 7150 },
    note: 'Empty property, standard spec, white accessories, average access'
  },
  {
    id: 'rewire-4-bed',
    propertyType: '4 Bed House',
    bedrooms: [4],
    propertyTypes: ['house', 'terraced', 'semi-detached', 'detached'],
    price: '£7,150 – £7,700',
    priceNumeric: 7150,
    priceRange: { min: 7150, max: 7700 },
    note: 'Empty property, standard spec, white accessories, average access'
  },
  {
    id: 'rewire-5-bed',
    propertyType: '5 Bed House',
    bedrooms: [5],
    propertyTypes: ['house', 'terraced', 'semi-detached', 'detached'],
    price: '£8,250 – £9,350+',
    priceNumeric: 8250,
    priceRange: { min: 8250, max: 9350 },
    note: 'Empty property, standard spec, white accessories, average access'
  }
];

// Rewire Pricing Modifiers
export const rewireModifiers = {
  occupiedProperty: {
    label: 'Occupied Property Uplift',
    price: '£400 – £800',
    priceRange: { min: 400, max: 800 },
    note: 'Additional cost depending on furniture moving, floor lifting complexity, and protection required'
  }
};

// Helper function to get rewire price based on property details
export const getRewiringPrice = (bedrooms, propertyType, isOccupied = false) => {
  // Find matching price
  const match = rewirePricing.find(item => {
    const bedroomMatch = item.bedrooms.includes(parseInt(bedrooms));
    const typeMatch = !propertyType || item.propertyTypes.includes(propertyType.toLowerCase());
    return bedroomMatch && typeMatch;
  });

  if (!match) {
    return {
      available: false,
      message: 'Individual quotation required',
      description: 'Your property requires a tailored quotation. Send us your details for an accurate price.'
    };
  }

  return {
    available: true,
    price: match.price,
    priceNumeric: match.priceNumeric,
    priceRange: match.priceRange,
    propertyType: match.propertyType,
    note: match.note,
    occupiedUplift: isOccupied ? rewireModifiers.occupiedProperty : null
  };
};

// EICR Pricing
export const eicrPricing = [
  { type: '1–2 Bed', price: '£140', priceNumeric: 140, note: 'Labour only' },
  { type: '3 Bed', price: '£170', priceNumeric: 170, note: 'Labour only' },
  { type: '4 Bed', price: '£200', priceNumeric: 200, note: 'Labour only' },
  { type: '5 Bed', price: '£240+', priceNumeric: 240, note: 'Labour only' },
  { type: 'HMO (up to 6 circuits)', price: 'From £200', priceNumeric: 200, note: 'Labour only' },
  { type: 'Each additional circuit', price: '£20', priceNumeric: 20, note: 'Labour only' }
];

// Consumer Unit Pricing
export const consumerUnitPricing = [
  {
    type: 'Standard RCBO Board with Surge Protection',
    price: 'From £550',
    priceNumeric: 550,
    subtitle: 'Starting from £550 for 5 circuit fuseboard',
    note: 'Labour + materials included. Price varies based on number of circuits.'
  }
];

// Fault Finding & Repairs
export const faultFindingPricing = [
  { item: 'First Hour Diagnostic', price: '£95', priceNumeric: 95 },
  { item: 'Each Additional Hour', price: '£75', priceNumeric: 75 },
  { item: 'Emergency Same Day Callout', price: '£140', priceNumeric: 140 }
];

// Small Works
export const smallWorksPricing = [
  { item: 'Replace socket/switch', price: '£65', priceNumeric: 65 },
  { item: 'Replace light fitting', price: '£75', priceNumeric: 75 },
  { item: 'Install downlight', price: '£85 each', priceNumeric: 85 },
  { item: 'Extractor fan replacement', price: '£120–£160', priceNumeric: 120 },
  { item: 'Smoke alarm installation', price: '£85 each', priceNumeric: 85 },
  { item: 'Outdoor light installation', price: '£120+', priceNumeric: 120 }
];

// Emergency Lighting
export const emergencyLightingPricing = [
  { item: 'Emergency Light Test (per fitting)', price: '£12', priceNumeric: 12 },
  { item: 'Annual Emergency Lighting Certificate', price: 'From £120', priceNumeric: 120 },
  { item: 'Full HMO Electrical Compliance Package', price: 'From £350', priceNumeric: 350 }
];

// Portfolio Discounts
export const portfolioDiscounts = [
  { properties: '3+', discount: '5%' },
  { properties: '6+', discount: '10%' }
];

// What's Included in Full Rewire
export const fullRewiringIncludes = [
  'Replacement of old cabling throughout the property',
  'New socket and switch wiring (standard white accessories fitted)',
  'Lighting rewired (standard fittings / downlights by request)',
  'New 18th Edition consumer unit with RCBO protection and SPD (as standard)',
  'Full testing and certification (Electrical Installation Certificate)'
];

// Rewire Best For
export const rewireBestFor = [
  'Properties with old or mixed wiring',
  'Renovations and refurbishments',
  'Landlords preparing for long-term tenancies',
  'Property developers and builders',
  'New property buyers',
  'Homes with recurring electrical faults or tripping issues'
];

// Partial Rewires
export const partialRewires = [
  'Kitchen rewires',
  'Extensions and loft conversions',
  'Upstairs or downstairs rewires',
  'Consumer unit upgrades and circuit improvements'
];

// Minimum starting price for marketing
export const startingFromPrice = '£4,950';
export const startingFromPriceContext = '1–2 bedroom flats';
